/// <amd-module name="Wit/Common/QueryService/IQueryService" />
import { createElement } from "react";
import { getTFSPageData } from "Tfs/Platform/Page";
import {
    IObservableArray,
    IObservableValue,
    IReadonlyObservableValue,
    IReadyableObservableArray,
    ObservableArray,
    ObservableValue,
    ReadyableObservableArray
} from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { ICancelablePromise, ICancelReason, makeCancelable } from "VSS/Core/Util/Promise";
import { isGuid } from "VSS/Core/Util/String";
import { HistoryBehavior, IPageStateHistoryService } from "VSS/Features/History";
import { FastPageSwitchEventDetail, IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { QueryExpand, QueryHierarchyItem, QueryType } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { getWorkItemTrackingClient } from "Wit/Clients/Wit/RestClient/WorkItemTracking";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { CoreFieldRefNames, FieldFlags, FieldUsages } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { QuerySaveDialog } from "Wit/Common/QuerySaveDialog/Components/QuerySaveDialog";
import { reportError } from "Wit/Common/QueryUtils/ErrorHandlingUtils";
import { QueryItem, QuerySaveDialogMode } from "Wit/Common/QueryUtils/Models";
import { TelemetryFeature } from "Wit/Common/QueryUtils/QueriesHubConstants";
import { LinkQueryMode } from "Wit/Common/QueryUtils/QueryTypeConstants";
import { checkFlag } from "Wit/Common/QueryUtils/QueryUtils";
import { getQueryUrl } from "Wit/Common/QueryUtils/RoutingUtils";
import { WiqlGenerator } from "Wit/Common/QueryUtils/WiqlGenerator";
import { TempQueryUtils } from "Wit/Common/Utilities/TempQueryUtils";
import { executeCallbackForTargetIdsInBatches } from "Wit/Common/WorkItemDialogs/Components/BatchExecutionHelper";
import { EditorDirtyDialog } from "Wit/Common/WorkItemDialogs/Components/EditorDirtyDialog";
import { IWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import {
    IEditInfo,
    IPartialEditInfo,
    IQueryDisplayColumn,
    IQueryParams,
    IQueryResultItem,
    IQueryResultItemData,
    IQueryResultResponse,
    IQuerySortColumn,
    IWorkItemLinkResponse,
    IWorkItemLinkType
} from "Wit/QueriesHub/Common/Interfaces";
import { IFieldProjectData } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItemDataManager } from "Wit/WorkItemDataManager/WorkItemDataManager";
import { PageSizes } from "Wit/WorkItemModel/OM/";
import { IWorkItem, IWorkItemPermissionData } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemEventData, IWorkItemEventService } from "Wit/WorkItemModel/Services/IWorkItemEventService";
import { IWorkItemFormUnsavedChangesService } from "Wit/WorkItemModel/Services/WorkItemFormUnsavedChangesService";
import { isComputed } from "Wit/WorkItemModel/WorkItem/FieldDefinitionUtils";

/**
 * Number of items to be returned for results tree
 * Initally returns 5000 unless specified for all items
 * Max number that can be returned from a query is 19999
 */
const AllResultCount = 19999;
const RecycleBinPersistenceId: string = "2650C586-0DE4-4156-BA0E-14BCFB664CCA";
interface AdHocQueryResult {
    data: {
        recycleBin: {
            id: string;
            query: string;
        };
    };
}

interface IPageWorkItemsDataProviderResponse {
    data: {
        columns: string[];
        rows: any[];
    };
    errorMessage?: string;
}

export interface IQueryColumn {
    id: string;
    name: string;
}

export const enum QueryHubView {
    QueryResults = "query",
    QueryEdit = "query-edit",
    QueryCharts = "query-charts",
    WorkItemEdit = "edit"
}

const QueriesLastVisitedKey = "Queries/LastVisited";
const SettingsScopeProject = "Project";

export interface IQueryService extends IVssService {
    allFields: ReadyableObservableArray<IFieldEntry>;
    canViewRecycleBin: boolean;
    editInfo: IObservableValue<IEditInfo>;
    editorFields: ReadyableObservableArray<IFieldEntry>;
    queryLoadingError: IObservableValue<string | undefined>;
    editorWorkItemLinkTypes: ObservableArray<IWorkItemLinkType>;
    fieldProjectData: IObservableValue<IFieldProjectData>;
    filteredQueryResultItems: ReadyableObservableArray<IQueryResultItem>;
    filteredQueryResultItemsMap: Map<number, IQueryResultItem>;
    isCrossProject: boolean;
    isEditorDirty: IObservableValue<boolean>;
    isNewQuery: boolean;
    hasQueryRun: IObservableValue<boolean>;
    isQueryRunning: IObservableValue<boolean>;
    isRecycleBin: boolean;
    lastEditedWorkItem: number | undefined;
    parentId: string;
    previousView: string | undefined;
    query: IObservableValue<QueryHierarchyItem | undefined>;
    queryColumns: ObservableArray<string>;
    queryId: string | undefined;
    queryResult: IReadonlyObservableValue<IQueryResultResponse | undefined>;
    queryResultItems: IReadyableObservableArray<IQueryResultItem>;
    queryResultItemsMap: Map<number, IQueryResultItem>;
    querySortColumns: IQuerySortColumn[];
    sortableFields: ReadyableObservableArray<IFieldEntry>;
    tempQueryId: string;
    urlWiql: string;
    view: IReadonlyObservableValue<QueryHubView | string>;
    wiql: IObservableValue<string>;
    workItems: IObservableArray<IWorkItem>;
    fetchedItemsIds: ObservableValue<{ [id: string]: number }>;
    totalNumberOfQueryResults: number;

    addResultListeners: () => void;
    changeView(newView: QueryHubView.WorkItemEdit, workItemId: number): void;
    changeView(newView: Exclude<QueryHubView, QueryHubView.WorkItemEdit>): void;
    changeView(newView: string): void;
    clearData(): void;
    executeQuery: (projectId: string, queryWiql?: string, runQuery?: boolean) => Promise<IQueryResultResponse | undefined>;
    executeIfNotExecuting: (projectId: string, queryWiql?: string) => Promise<void>;
    fetchWorkItems: (itemIds: number[], options?: { skipUsingOfBatches?: boolean }) => Promise<void>;
    fetchWorkItem: (itemId: number) => void;
    generateWiql: (projectId: string, editInfo?: IEditInfo) => Promise<string>;
    getQuery(projectId: string): Promise<QueryHierarchyItem | undefined>;
    getQueryId: () => string;
    getQueryColumns: (projectId: string) => Promise<IQueryDisplayColumn[]>;
    getQueryEditInfo: (queryWiql: string, queryId?: string) => Promise<IQueryResultResponse | undefined>;
    getQueryType: (editInfo?: IPartialEditInfo) => QueryType;
    getLinkTypes: () => Promise<IWorkItemLinkResponse | undefined>;
    isWorkItemAlreadyFetched: (currentData: IQueryResultItem | undefined) => boolean;
    initializeFields: (projectId?: string) => Promise<void>;
    removeResultListeners: () => void;
    revertQuery: () => Promise<void>;
    saveQuery: (projectId: string) => Promise<void>;
    setQueryIdMRU: () => void;
    /**
     * reset list of fetched items
     */
    clearFetchedItems: () => void;
    getNotFetchedWorkItemIds: (targetIds: number[], nextToFetchNumberOfWorkItems: number) => number[];

    removeWorkItems: (itemsIds: Set<number>) => void;
    getParentMap: () => Map<number, IQueryResultItemData>;
}

export namespace QueryDataProviders {
    export const AdHocQueryDataProvider = "ms.vss-work-web.ad-hoc-queries-data-provider";
    export const QueryDataProvider = "ms.vss-work-web.work-item-query-data-provider";
    export const WorkItemTagSuggestionsDataProvider = "ms.vss-work-web.work-item-tag-suggestions-data-provider";
    export const WorkItemMetadataLinkTypesDataProvider = "ms.vss-work-web.workitem-link-types";
    export const WorkItemPermissionsDataProvider = "ms.vss-work-web.work-item-permissions-data-provider";
    export const PageWorkItemsDataProvider = "ms.vss-work-web.page-work-items-data-provider";
}

class QueryService extends VssService implements IQueryService {
    public previousView: string | undefined;
    public parentId: string;
    public queryId: string | undefined;
    public queryResult = new ObservableValue<IQueryResultResponse | undefined>(undefined);
    public queryResultItems = new ReadyableObservableArray<IQueryResultItem>([], true);
    public queryResultItemsMap = new Map<number, IQueryResultItem>();
    public fieldProjectData = new ObservableValue<IFieldProjectData>({ projects: [], fields: [] });
    public filteredQueryResultItems = new ReadyableObservableArray<IQueryResultItem>([]);
    public filteredQueryResultItemsMap = new Map<number, IQueryResultItem>();
    public isEditorDirty = new ObservableValue(false);
    public hasQueryRun = new ObservableValue(false);
    public isQueryRunning = new ObservableValue(false);
    public editInfo = new ObservableValue<IEditInfo>(this.getEditInfo());
    public editorFields = new ReadyableObservableArray<IFieldEntry>([]);
    public queryLoadingError: IObservableValue<string | undefined> = new ObservableValue(undefined);
    public allFields = new ReadyableObservableArray<IFieldEntry>([]);
    public sortableFields = new ReadyableObservableArray<IFieldEntry>([]);
    public editorWorkItemLinkTypes = new ObservableArray<IWorkItemLinkType>([]);
    public urlWiql: string;
    public view = new ObservableValue<QueryHubView | string>(QueryHubView.QueryResults);
    public wiql = new ObservableValue<string>("");
    public query = new ObservableValue<QueryHierarchyItem | undefined>(undefined);
    public tempQueryId: string;
    public workItems = new ObservableArray<IWorkItem>([]);
    public lastEditedWorkItem: number | undefined;
    private _executeQueryPromise: ICancelablePromise<IQueryResultResponse | undefined> | undefined;
    private _workItemDataManager: IWorkItemDataManager;
    private _projectId?: string;
    private _projectName?: string;
    private _listenerSubscriptionCount = 0;
    public isRecycleBin: boolean = false;
    public canViewRecycleBin = false;
    public fetchedItemsIds = new ObservableValue<{ [id: string]: number }>({});
    public totalNumberOfQueryResults = 0;
    private debouncedFetchWorkItems: () => void;
    private itemsBeingFetched: number[] = [];
    // all: any item that needed to be fetched at any time in the past
    // waitList: LIFO queue for items that must still be fetched
    private itemsToFetch: { all: { [itemId: number]: number | undefined }; waitList: number[] } = { all: {}, waitList: [] };
    private timerManagement = new TimerManagement();
    private pageStateHistoryService: IPageStateHistoryService;
    private isDeletedField: IFieldEntry | undefined;
    private parentMap: Map<number, IQueryResultItemData> = new Map();
    private columnOptionsFilteringAndOptimizationEnabled: boolean;

    private _defaultColumns: string[] = [
        CoreFieldRefNames.Id,
        CoreFieldRefNames.WorkItemType,
        CoreFieldRefNames.Title,
        CoreFieldRefNames.AssignedTo,
        CoreFieldRefNames.State,
        CoreFieldRefNames.Tags
    ];

    private queryColumnsInResultsPayload = new ObservableArray<string>([...this._defaultColumns]);
    public queryColumns = new ObservableArray<string>([...this._defaultColumns]);
    public querySortColumns: IQuerySortColumn[] = [];

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);
        this.initialize();
        this.debouncedFetchWorkItems = this.timerManagement.debounce(this.doFetchWorkItems, 200);
    }

    public _serviceEnd(pageContext: IVssPageContext) {
        super._serviceEnd(pageContext);
        this.removeResultListeners(/** force */ true);
    }

    private async initialize() {
        this._workItemDataManager = this.pageContext.getService<IWorkItemDataManager>("IWorkItemDataManager");
        const navigationService = this.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        const historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const routeValues = historyService.getState();
        const tempQueryId = routeValues["tempQueryId"];
        const wiql = routeValues["wiql"];
        const navigation = navigationService.getDisplayedNavigation();
        const queryId = routeValues["id"];
        const isEditWorkItem = routeValues["view"] === "edit";

        this.columnOptionsFilteringAndOptimizationEnabled = isFeatureFlagEnabled(
            this.pageContext,
            FeatureFlags.ColumnOptionsFilteringAndOptimization,
            false
        );

        const project = getTFSPageData(this.pageContext)?.project;
        this._projectId = project?.id;
        this._projectName = project?.name;
        this.editInfo.value = { ...this.editInfo.value, teamProject: project?.name || "" };

        this.pageStateHistoryService = this.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        this.pageStateHistoryService.registerObservable(this.view, "view", HistoryBehavior.newEntry);

        this.isRecycleBin = routeValues["path"] === "Recycle Bin";
        const isQueriesHub = navigation.some(n => n === "ms.vss-work-web.new-queries-hub");

        if (isQueriesHub && this.isRecycleBin) {
            await this.getPermissions();

            let adHocQueryResult: AdHocQueryResult | undefined = undefined;
            try {
                const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
                adHocQueryResult = await contributionService.getDataAsync<AdHocQueryResult>(QueryDataProviders.AdHocQueryDataProvider);
            } catch (error) {
                reportError(this.pageContext, TelemetryFeature, error.message);
                this.wiql.value = "";
            }

            const recycleBinQuery = adHocQueryResult?.data.recycleBin;
            if (recycleBinQuery) {
                this.queryId = recycleBinQuery.id;

                this.wiql.value = await this.generateWiqlInternal(this.editInfo.value, this._projectId ?? "", adHocQueryResult);

                this.query.value = { id: recycleBinQuery.id, wiql: this.wiql.value } as QueryHierarchyItem;
                if (this.canViewRecycleBin) {
                    this.executeQuery(this._projectId ?? "", this.wiql.value);
                } else {
                    this.hasQueryRun.value = true;
                    this.queryResult.value = undefined;
                    this.queryResultItems.value = [];
                    this.queryResultItemsMap = new Map<number, IQueryResultItem>();
                    this.queryResultItems.ready.value = true;
                    this.isQueryRunning.value = false;
                }
                return;
            }
        }

        if (!isEditWorkItem) {
            this.queryId = queryId;
            this.tempQueryId = tempQueryId;
            this.parentId = routeValues["parentId"];
            this.urlWiql = wiql;

            if (wiql && !this.wiql.value) {
                this.wiql.value = wiql;
            }
        } else {
            this.queryId = routeValues["queryId"];
        }

        this.view.value = this.getCurrentView();
    }

    public addResultListeners() {
        if (++this._listenerSubscriptionCount !== 1) {
            return;
        }

        document.body.addEventListener("fpsStarting", this.onFpsStarting);
        window.addEventListener("beforeunload", this.onBeforeUnload);
        const eventService = this.pageContext.getService<IWorkItemEventService>("IWorkItemEventService");
        eventService.subscribe(this.onWorkItemChanged);
        const unsavedChangesService = this.pageContext.getService<IWorkItemFormUnsavedChangesService>("IWorkItemFormUnsavedChangesService");
        unsavedChangesService.registerFPSFilter(this.isQueryResultsFPS);
    }

    public removeResultListeners(force = false) {
        if (!force) {
            if (this._listenerSubscriptionCount === 0 || --this._listenerSubscriptionCount !== 0) {
                return;
            }
        } else {
            this._listenerSubscriptionCount = 0;
        }

        document.body.removeEventListener("fpsStarting", this.onFpsStarting);
        window.removeEventListener("beforeunload", this.onBeforeUnload);
        const eventService = this.pageContext.getService<IWorkItemEventService>("IWorkItemEventService");
        eventService.unsubscribe(this.onWorkItemChanged);
    }

    private getPermissions = async () => {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const permissions = await contributionService.getDataAsync<IWorkItemPermissionData>(QueryDataProviders.WorkItemPermissionsDataProvider);
        this.canViewRecycleBin = !!permissions?.queryViewPermission?.hasPermission;
    };

    private onBeforeUnload = (e: BeforeUnloadEvent): void => {
        if (this.isEditorDirty.value) {
            e.returnValue = "dirtyState";
            e.preventDefault();
        }
    };

    private onFpsStarting = (e: CustomEvent<FastPageSwitchEventDetail>): void => {
        if (
            this.isEditorDirty.value &&
            !(e.detail.data && (e.detail.data.isQueryResultsFPS || e.detail.data.isQuerySaveFPS || e.detail.data.userConfirmed))
        ) {
            e.preventDefault();
            e.detail.cancellationReason = "dirtyState";
            this.showDirtyDialog(() => {
                this.isEditorDirty.value = false;
                FastPageSwitch(e.detail.pageContext, e.detail.href, e.detail.recordHistoryEntry, { ...e.detail.data, userConfirmed: true });

                // Persistent services live between FPSs, so we do a force clean up
                // to make sure we don't leak a subscription on navigation to a different hub
                this.removeResultListeners(/** force */ true);
            });
        }
    };

    private isQueryResultsFPS = (e: CustomEvent<FastPageSwitchEventDetail>): boolean => {
        return !(e.detail.data && e.detail.data.isQueryResultsFPS);
    };

    private showDirtyDialog = (onLeave: () => void) => {
        const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss =>
            createElement(EditorDirtyDialog, {
                onDismiss,
                onLeave,
                onSave: () => this._projectId && this.saveQueryOnFPS(this._projectId, onLeave),
                query: this.query.value
            })
        );
    };

    private saveQueryOnFPS = async (projectId: string, onLeave: () => void) => {
        const query = await this.getQuery(projectId);
        if (query) {
            await this.saveQuery(projectId);
            onLeave();
        } else {
            // Save new query case
            const wiql = await this.generateWiql(projectId);
            const layoutManager = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            layoutManager.renderCallout(() =>
                createElement(QuerySaveDialog, {
                    mode: QuerySaveDialogMode.NewQuery,
                    onDismiss: onLeave,
                    queryItem: { isFolder: false, wiql, path: "" } as QueryItem,
                    navigateToQueryOnSave: false
                })
            );
        }
    };

    public async getQuery(projectId: string): Promise<QueryHierarchyItem | undefined> {
        if (!this.query.value && !this.isRecycleBin) {
            const queryId = this.getQueryId();
            const tempQueryId = this.getTemporaryQueryId();
            let updatedWiql = "";
            if (queryId) {
                const witClient = getWorkItemTrackingClient(this.pageContext);
                try {
                    this.query.value = await witClient.getQuery(projectId, queryId, QueryExpand.All, undefined, undefined, true);
                    const editInfoResult = await this.getQueryEditInfo(this.query.value.wiql, queryId);
                    if (editInfoResult && editInfoResult.data && editInfoResult.data.editInfo && !this.isEditorDirty.value) {
                        const editInfo = editInfoResult.data.editInfo;
                        this.editInfo.value = this.getEditInfo(editInfo);
                    }
                    updatedWiql = this.query.value.wiql;
                } catch (error) {
                    error = error as Error;
                    if (!this.queryLoadingError.value) {
                        reportError(this.pageContext, TelemetryFeature, error.message);
                    }

                    if (!this.queryLoadingError.value && error.serverError?.typeKey === "QueryItemNotFoundException") {
                        this.queryLoadingError.value = error.message;
                    }
                }
            } else if (tempQueryId) {
                const tempQuery = await TempQueryUtils.getTemporaryQuery(this.pageContext, tempQueryId);
                const editInfoResult = await this.getQueryEditInfo(tempQuery.queryText, tempQueryId);
                if (editInfoResult && editInfoResult.data && editInfoResult.data.editInfo && !this.isEditorDirty.value) {
                    const editInfo = editInfoResult.data.editInfo;
                    this.editInfo.value = this.getEditInfo(editInfo);
                }
                updatedWiql = tempQuery.queryText;
            } else if (this.urlWiql) {
                const editInfoResult = await this.getQueryEditInfo(this.urlWiql);
                if (editInfoResult && editInfoResult.data && editInfoResult.data.editInfo && !this.isEditorDirty.value) {
                    const editInfo = editInfoResult.data.editInfo;
                    this.editInfo.value = this.getEditInfo(editInfo);
                }
                updatedWiql = this.urlWiql;
            }

            if (updatedWiql && updatedWiql !== this.wiql.value) {
                this.wiql.value = updatedWiql;
            }

            // ensures this.editorWorkItemLinkTypes is populated
            await this.getLinkTypes();
        }
        return this.query.value;
    }

    public get isCrossProject(): boolean {
        return !this.editInfo.value.teamProject;
    }

    public get isNewQuery(): boolean {
        return !this.queryId;
    }

    private getEditInfo(editInfo?: IPartialEditInfo): IEditInfo {
        if (editInfo) {
            return {
                mode: editInfo.mode,
                treeLinkTypes: editInfo.treeLinkTypes ?? "",
                linkTypes: editInfo.linkTypes ?? "",
                sourceFilter: {
                    clauses: [...editInfo.sourceFilter.clauses],
                    groups: [...editInfo.sourceFilter.groups]
                },
                treeTargetFilter: editInfo.treeTargetFilter
                    ? {
                          clauses: [...editInfo.treeTargetFilter.clauses],
                          groups: [...editInfo.treeTargetFilter.groups]
                      }
                    : { clauses: [], groups: [] },
                linkTargetFilter: editInfo.linkTargetFilter
                    ? {
                          clauses: [...editInfo.linkTargetFilter.clauses],
                          groups: [...editInfo.linkTargetFilter.groups]
                      }
                    : { clauses: [], groups: [] },
                teamProject: editInfo.teamProject
            };
        } else {
            return {
                mode: LinkQueryMode.WorkItems,
                treeLinkTypes: "",
                linkTypes: "",
                sourceFilter: { clauses: [], groups: [] },
                treeTargetFilter: { clauses: [], groups: [] },
                linkTargetFilter: { clauses: [], groups: [] },
                teamProject: this._projectName ?? ""
            };
        }
    }

    public revertQuery = async () => {
        if (this.query.value) {
            const queryId = this.getQueryId();
            const editInfoResult = await this.getQueryEditInfo(this.query.value.wiql, queryId);
            if (editInfoResult && editInfoResult.data && editInfoResult.data.editInfo) {
                const editInfo = editInfoResult.data.editInfo;
                this.editInfo.value = this.getEditInfo(editInfo);
            }
        } else if (this.tempQueryId) {
            const tempQuery = await TempQueryUtils.getTemporaryQuery(this.pageContext, this.tempQueryId);
            const editInfoResult = await this.getQueryEditInfo(tempQuery.queryText, this.tempQueryId);
            if (editInfoResult && editInfoResult.data && editInfoResult.data.editInfo) {
                const editInfo = editInfoResult.data.editInfo;
                this.editInfo.value = this.getEditInfo(editInfo);
            }
        } else if (this.urlWiql) {
            const editInfoResult = await this.getQueryEditInfo(this.urlWiql);
            if (editInfoResult && editInfoResult.data && editInfoResult.data.editInfo) {
                const editInfo = editInfoResult.data.editInfo;
                this.editInfo.value = this.getEditInfo(editInfo);
            }
        } else {
            this.editInfo.value = this.getEditInfo();
        }
        this.isEditorDirty.value = false;
    };

    public async saveQuery(projectId: string) {
        const restClient = getWorkItemTrackingClient(this.pageContext);
        const wiql = await this.generateWiql(projectId);
        const query = await this.getQuery(projectId);
        if (query) {
            const newQuery = {
                id: query.id,
                name: query.name,
                path: query.path,
                wiql: wiql
            } as QueryHierarchyItem;
            const retVal = await restClient.updateQuery(newQuery, projectId, query.id);
            this.query.value = retVal;
            if (wiql && wiql.length !== 0) {
                this.isEditorDirty.value = false;
            }
            await this.executeQuery(projectId, wiql);
        }
    }

    public setQueryIdMRU() {
        const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");
        settingsService.setEntries({ [QueriesLastVisitedKey]: this.getQueryId() }, SettingsUserScope.Me, SettingsScopeProject, this._projectId ?? "");
    }

    public clearData(): void {
        this.queryResult.value = undefined;
        this.queryResultItems.value = [];
        this.queryResultItemsMap.clear();
    }

    public clearFetchedItems(): void {
        this.itemsToFetch = { all: {}, waitList: [] };
    }

    public async getQueryEditInfo(queryWiql: string, queryId?: string): Promise<IQueryResultResponse | undefined> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const wiql: string = queryWiql;
        const runQuery: boolean = false;
        const includePayload: boolean = false;
        const useIsoDateFormat = true;
        const requestParams: IQueryParams = {
            wiql,
            runQuery,
            includePayload,
            useIsoDateFormat,
            persistenceId: queryId
        };
        return contributionService.getDataAsync<IQueryResultResponse>(QueryDataProviders.QueryDataProvider, requestParams, /* ignoreCache */ true);
    }

    public async executeIfNotExecuting(projectId: string, queryWiql?: string): Promise<void> {
        if (!this._executeQueryPromise) {
            await this.executeQuery(projectId, queryWiql);
        }
    }

    public async executeQuery(projectId: string, queryWiql?: string, showAllItems?: boolean): Promise<IQueryResultResponse | undefined> {
        this.queryResultItems.ready.value = false;
        this.isQueryRunning.value = true;

        const query = await this.getQuery(projectId);
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const wiql = queryWiql ?? (query?.wiql || "");
        const queryId = this.isRecycleBin ? RecycleBinPersistenceId : query?.id;
        const requestParams: IQueryParams = {
            useIsoDateFormat: true,
            wiql,
            top: showAllItems ? AllResultCount : undefined,
            persistenceId: queryId,
            isDirty: this.isEditorDirty.value
        };

        this.setQueryIdMRU();

        if (!this.editorFields.ready.value) {
            await this.initializeFields(projectId);
        }

        try {
            if (this._executeQueryPromise) {
                this._executeQueryPromise.cancel();
            }

            this._executeQueryPromise = makeCancelable(
                contributionService.getDataAsync<IQueryResultResponse>(QueryDataProviders.QueryDataProvider, requestParams, true)
            );
            const queryResultResponse = await this._executeQueryPromise.promise;
            if (queryResultResponse?.data?.payload?.rows && queryResultResponse.data.payload.columns && queryResultResponse.data.editInfo) {
                const columns = queryResultResponse.data.payload.columns;
                const rows = queryResultResponse.data.payload.rows;
                const queryResultItems: IQueryResultItem[] = rows.map(r => getQueryResultItem(columns, r));
                const queryType = this.getQueryType(queryResultResponse.data.editInfo);

                this.totalNumberOfQueryResults =
                    queryType === QueryType.OneHop ? new Set(queryResultResponse.data.targetIds).size : queryResultResponse.data.targetIds.length;

                const fetchedItemsIds = queryResultItems.reduce((result, queryResultItem) => {
                    const id = queryResultItem.value[CoreFieldRefNames.Id];
                    result[id] = id;
                    return result;
                }, {} as { [id: string]: number });
                this.fetchedItemsIds.value = fetchedItemsIds;

                //we need to fill queryResultItemsMap from fetched workitems without duplicates (before insertPlaceholderQueryItems)
                this.queryResultItemsMap = new Map(queryResultItems.map(obj => [+obj.value[CoreFieldRefNames.Id], obj]));
                insertPlaceholderQueryItems(queryResultResponse.data.targetIds ?? [], queryResultItems, columns, this.queryResultItemsMap);

                this.queryColumnsInResultsPayload.value = columns;

                this.queryResultItems.value = queryResultItems;
                this.wiql.value = queryResultResponse.data.wiql;
                this.editInfo.value = this.getEditInfo(queryResultResponse.data.editInfo);
            } else {
                this.queryResultItemsMap = new Map<number, IQueryResultItem>();
                this.queryResultItems.value = [];
            }
            if (queryResultResponse?.data?.error) {
                reportError(this.pageContext, TelemetryFeature, queryResultResponse.data.error);
            }

            if (queryResultResponse) {
                this.queryResult.value = queryResultResponse;
            }

            this.queryColumns.value = this.queryResult.value?.data?.columns.map(column => column.name ?? "") || [...this._defaultColumns];
            this.querySortColumns = this.queryResult.value?.data?.sortColumns || [];

            if (this.queryColumns.value.some(c => c === CoreFieldRefNames.Parent)) {
                await this.fetchOrUpdateParentMap(this.queryResultItems.value);
            }

            this.isQueryRunning.value = false;
            this.hasQueryRun.value = true;
            this.queryResultItems.ready.value = true;
            return this.queryResult.value;
        } catch (ex) {
            // if the promise has been canceled, we don't really care anymore
            if (!(ex as ICancelReason).isCanceled) {
                // todo: we should probably do something
                throw ex;
            }
        } finally {
            this._executeQueryPromise = undefined;
        }
    }

    public getQueryType = (editInfo?: IPartialEditInfo): QueryType => {
        const valueToCheck = editInfo?.mode ?? this.queryResult.value?.data?.editInfo?.mode ?? LinkQueryMode.WorkItems;
        return LinkQueryMode.getQueryType(valueToCheck);
    };

    private isQueryableField = (field: IFieldEntry): boolean => {
        const workItemUsage = FieldUsages.WorkItem;
        if (checkFlag(field.flags, FieldFlags.Queryable) && field.usages & workItemUsage) {
            return true;
        }
        return false;
    };

    private isSortableField = (field: IFieldEntry): boolean => {
        return checkFlag(field.flags, FieldFlags.Sortable);
    };

    public async initializeFields(projectId?: string) {
        let fieldProjectData: IFieldProjectData | null;
        let fields: IFieldEntry[] | null = null;
        if (this.columnOptionsFilteringAndOptimizationEnabled) {
            const fieldProjectDataPromise = this._workItemDataManager.beginGetFieldProjectData(projectId ?? "");
            const fieldEntriesPromise = this._workItemDataManager.beginGetFields();
            const fieldIdMap: { [id: number]: boolean } = {};

            fieldProjectData = await fieldProjectDataPromise;
            fieldProjectData?.projects?.forEach(proj => {
                if (proj.fieldIds) {
                    for (const fieldId of proj.fieldIds) {
                        if (!(fieldId in fieldIdMap)) {
                            fieldIdMap[fieldId] = true;
                        }
                    }
                }
            });

            const fieldEntries = await fieldEntriesPromise;
            if (fieldEntries?.length) {
                fields = fieldEntries?.filter(entry => fieldIdMap[entry.id]);
            }
        } else {
            fieldProjectData = await this._workItemDataManager.beginGetFieldProjectData(projectId ?? "");
            fields = await this._workItemDataManager.beginGetFields();
        }

        if (fields?.length && fieldProjectData?.fields?.length && fieldProjectData?.projects?.length) {
            this.fieldProjectData.value.projects = fieldProjectData.projects;
            this.fieldProjectData.value.fields = fieldProjectData.fields
                .filter(field => field.referenceName !== CoreFieldRefNames.TeamProject && this.isQueryableField(field))
                .sort((f1, f2) => f1.name.localeCompare(f2.name, navigator.language));
            const sortedFields = fields.sort((f1, f2) => f1.name.localeCompare(f2.name, navigator.language));
            this.editorFields.value = sortedFields.filter(field => this.isQueryableField(field));
            this.editorFields.ready.value = true;
            this.sortableFields.value = sortedFields.filter(field => this.isSortableField(field));
            this.sortableFields.ready.value = true;
            this.allFields.value = sortedFields;
            this.allFields.ready.value = true;
        }
    }

    public isWorkItemAlreadyFetched = (currentData: IQueryResultItem | undefined): boolean => {
        return currentData?.value[CoreFieldRefNames.State] !== undefined;
    };

    public fetchWorkItem = (itemId: number): void => {
        // if item is not found, track it and then push it to the front
        // that way the most recent items will get fetched
        if (!this.itemsToFetch.all[itemId]) {
            this.itemsToFetch.all[itemId] = itemId;
            this.itemsToFetch.waitList.unshift(itemId);

            // this will fetch all items in itemsToFetch after 200ms pass without any calls to fetchWorkItem
            this.debouncedFetchWorkItems();
        }
    };

    private doFetchWorkItems = async (): Promise<void> => {
        // if we're already fetching items, do nothing
        if (this.itemsBeingFetched.length > 0) {
            return;
        }

        // take up to the first 200 missing items, if there are more we will schedule another fetch after this returns
        this.itemsBeingFetched = this.itemsToFetch.waitList.splice(0, PageSizes.QUERY);

        const retrievedItems = await this.fetchWorkItemsCore(this.itemsBeingFetched);

        if (retrievedItems) {
            if (this.queryColumns.value.some(c => c === CoreFieldRefNames.Parent)) {
                await this.fetchOrUpdateParentMap(retrievedItems);
            }

            this.updateQueryResultsItems(retrievedItems);
        }

        this.updateFetchedItemsIds(this.itemsBeingFetched);

        this.itemsBeingFetched = [];

        if (retrievedItems && this.itemsToFetch.waitList.length > 0) {
            this.debouncedFetchWorkItems();
        }
    };

    public fetchWorkItems = async (targetIds: number[], options?: { skipUsingOfBatches?: boolean }): Promise<void> => {
        const itemsToFetch: number[] = [];
        for (const workItemId of targetIds) {
            const queryResultItem = this.queryResultItemsMap.get(workItemId);
            if (!this.isWorkItemAlreadyFetched(queryResultItem)) {
                itemsToFetch.push(workItemId);
            }
        }

        const retrievedItems: IQueryResultItem[] = [];

        let succeeded = true;

        if (options?.skipUsingOfBatches) {
            const batchOfRetrievedItems = await this.fetchBatchOfWorkItems(targetIds);
            if (!batchOfRetrievedItems) {
                succeeded = false;
            } else {
                retrievedItems.push(...batchOfRetrievedItems);
            }
        } else {
            succeeded = await executeCallbackForTargetIdsInBatches(targetIds, async subsetTargetIds => {
                const batchOfRetrievedItems = await this.fetchBatchOfWorkItems(subsetTargetIds);
                if (batchOfRetrievedItems === undefined) {
                    return false;
                }

                retrievedItems.push(...batchOfRetrievedItems);
                return true;
            });
        }

        if (succeeded) {
            if (this.queryColumns.value.some(c => c === CoreFieldRefNames.Parent)) {
                await this.fetchOrUpdateParentMap(retrievedItems);
            }

            // updating queryResultItems after fetching of all workitems in order to avoid unnecessary rerendering of results tree during filtering
            this.updateQueryResultsItems(retrievedItems);
        }

        this.updateFetchedItemsIds(targetIds);
    };

    private fetchBatchOfWorkItems = async (itemIds: number[]): Promise<IQueryResultItem[] | undefined> => {
        if (itemIds.length === 0) {
            return [];
        }

        //remove from itemsToFetch
        for (const workItemId of itemIds) {
            if (this.itemsToFetch.all[workItemId]) {
                this.itemsToFetch.all[workItemId] = undefined;

                const index = this.itemsToFetch.waitList.indexOf(workItemId);
                this.itemsToFetch.waitList.splice(index, 1);
            }
        }

        return this.fetchWorkItemsCore(itemIds);
    };

    private fetchWorkItemsCore = async (itemIds: number[]): Promise<IQueryResultItem[] | undefined> => {
        if (itemIds.length === 0) {
            return [];
        }

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

        const response = await contributionService.getDataAsync<IPageWorkItemsDataProviderResponse>(
            QueryDataProviders.PageWorkItemsDataProvider,
            {
                fields: this.queryColumnsInResultsPayload.value.join(","),
                workItemIds: itemIds.join(","),
                isDeleted: this.isRecycleBin
            },
            true /* ignore cache */
        );

        if (response && response.errorMessage) {
            reportError(this.pageContext, "page-work-items-queries-hub", response.errorMessage);
            return undefined;
        } else if (response) {
            const columns = response.data.columns;
            const rows = response.data.rows;

            const retrievedItems = rows.map(r => getQueryResultItem(columns, r));
            return retrievedItems;
        }
    };

    private updateQueryResultsItems(retrievedItems: IQueryResultItem[]) {
        const elementsThatWillBeUpdated = new Map<number, number>();

        for (let i = 0; i < retrievedItems.length; i++) {
            const id = retrievedItems[i].value[CoreFieldRefNames.Id];
            const index = this.queryResultItems.value.findIndex(item => item.value[CoreFieldRefNames.Id] === id);
            if (index > -1) {
                elementsThatWillBeUpdated.set(index, i);
            }

            this.queryResultItemsMap.set(+id, retrievedItems[i]);
        }

        const newQueryResultItems: IQueryResultItem[] = [];

        // do not change IQueryResultItem.value directly because that will cause unnecessary rerender of resulttree row
        for (let i = 0; i < this.queryResultItems.value.length; i++) {
            const mapIndex = elementsThatWillBeUpdated.get(i);
            if (mapIndex !== undefined) {
                newQueryResultItems.push(retrievedItems[mapIndex]);
            } else {
                newQueryResultItems.push(this.queryResultItems.value[i]);
            }
        }

        // replace entire object to trigger observable
        this.queryResultItems.value = newQueryResultItems;
    }

    public async getLinkTypes(): Promise<IWorkItemLinkResponse | undefined> {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const linkTypeData = await contributionService.getDataAsync<IWorkItemLinkResponse>(QueryDataProviders.WorkItemMetadataLinkTypesDataProvider);
        if (linkTypeData?.data) {
            this.editorWorkItemLinkTypes.value = linkTypeData.data.linkTypes;
        }
        return linkTypeData;
    }

    public getQueryId(): string {
        const historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const routeValues = historyService.getState();
        const idQueryParam = routeValues["view"] !== "edit" ? routeValues["id"] : routeValues["queryId"];
        return isGuid(idQueryParam) ? idQueryParam : "";
    }

    private getTemporaryQueryId(): string {
        const historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const routeValues = historyService.getState();
        const tempQueryId = routeValues["tempQueryId"];
        return tempQueryId;
    }

    public async getQueryColumns(projectId: string): Promise<IQueryDisplayColumn[]> {
        if (!this.queryResult.value) {
            await this.executeIfNotExecuting(projectId);
        }
        if (!this.queryResult.value?.data?.columns) {
            throw Error("could not load columns"); // TODO
        }
        return this.queryResult.value.data.columns;
    }

    public generateWiql = async (projectId: string, editInfo?: IEditInfo): Promise<string> => {
        const wiql = await this.generateWiqlInternal(editInfo, projectId);
        if (wiql && wiql.length !== 0 && !editInfo) {
            this.wiql.value = wiql;
        }
        return editInfo ? wiql : this.wiql.value;
    };

    private async generateWiqlInternal(editInfo: IEditInfo | undefined, projectId: string, adHocQueryResult?: AdHocQueryResult) {
        let wiql = "";
        if (this.isRecycleBin && !this.wiql.value) {
            if (!this.canViewRecycleBin) {
                return wiql;
            }

            const recycleBinQuery = adHocQueryResult?.data.recycleBin;

            if (recycleBinQuery) {
                wiql = recycleBinQuery.query;
            } else {
                const recycleBinWiql: string[] = [];
                recycleBinWiql.push(WiqlGenerator.generateSelectClause(this.queryColumns.value));
                recycleBinWiql.push("FROM WorkItems");
                recycleBinWiql.push(`WHERE [System.TeamProject] = @project AND [System.IsDeleted] = true
                                                AND ([System.WorkItemType] NOT IN GROUP 'Microsoft.TestPlanCategory' AND [System.WorkItemType] NOT IN GROUP 'Microsoft.TestSuiteCategory'
                                                     AND [System.WorkItemType] NOT IN GROUP 'Microsoft.TestCaseCategory' AND [System.WorkItemType] NOT IN GROUP 'Microsoft.SharedParameterCategory'
                                                     AND [System.WorkItemType] NOT IN GROUP 'Microsoft.SharedStepCategory')`);
                recycleBinWiql.push(WiqlGenerator.generateOrderByClause(this.querySortColumns, false));
                wiql = recycleBinWiql.join(" ");
            }

            return wiql;
        }
        const tempQueryId = this.getTemporaryQueryId();
        if ((this.queryId || tempQueryId) && !this.isEditorDirty.value) {
            const query = await this.getQuery(projectId);
            wiql = query?.wiql || "";
        } else {
            try {
                if (!this.editorFields.ready.value) {
                    await this.initializeFields(projectId);
                }
                // ensures this.editorWorkItemLinkTypes is populated
                await this.getLinkTypes();

                if (!this.isDeletedField) {
                    let fields : IFieldEntry[] | undefined;
                    if (isFeatureFlagEnabled(this.pageContext, FeatureFlags.UseNewWorkItemFieldService, false)) {
                        const fieldService = this.pageContext.getService<IWorkItemFieldService2>("IWorkItemFieldService2");
                        fields = await fieldService.loadFields(); 
                    } else {
                        const fieldService = this.pageContext.getService<IWorkItemFieldService>("IWorkItemFieldService");
                        fields = await fieldService.getFieldsAsync();
                    }
                    this.isDeletedField = fields?.find(field => field.referenceName === "System.IsDeleted");
                }

                const isDeletedField = this.isRecycleBin ? this.isDeletedField : undefined;

                wiql = WiqlGenerator.generateWiql(
                    this.editorFields.value,
                    editInfo || this.editInfo.value,
                    this.queryColumns.value,
                    this.querySortColumns,
                    this.editorWorkItemLinkTypes.value,
                    isDeletedField
                );
            } catch (error) {
                reportError(this.pageContext, TelemetryFeature, error.message);
                this.wiql.value = "";
            }
        }
        return wiql;
    }

    private onWorkItemChanged = ({ workItem }: IWorkItemEventData): void => {
        const resultObservable = this.queryResultItemsMap.get(workItem.id);

        if (!resultObservable) {
            return;
        }

        // process all fields that are being displayed
        let hasUpdate = false;
        const result = resultObservable.value;
        for (const fieldRefName of Object.keys(result)) {
            let currentValue;
            const fieldDefinition = workItem.getFieldByName(fieldRefName)?.fieldDefinition;
            const computed = fieldDefinition && isComputed(fieldDefinition);
            let shouldCheckNonComputedValue = !computed;
            if (computed) {
                const computedValue = workItem.getComputedFieldValue(fieldDefinition.id);
                if (computedValue?.success) {
                    currentValue = computedValue.value;
                } else {
                    shouldCheckNonComputedValue = true;
                }
            }
            if (shouldCheckNonComputedValue) {
                currentValue = workItem.getFieldValueByName(
                    fieldRefName,
                    undefined /* original */,
                    undefined /* omitSetByRule */,
                    undefined /* asIdentifyRef */,
                    true /* convertToExternal */
                );
            }

            const oldValue = result[fieldRefName];
            if (currentValue !== oldValue) {
                result[fieldRefName] = currentValue;
                hasUpdate = true;
            }
        }

        if (result.isDirty !== workItem.dirty.value) {
            result.isDirty = workItem.dirty.value;
            hasUpdate = true;
        }

        if (result.isInvalid !== !workItem.isValid()) {
            result.isInvalid = !workItem.isValid();
            hasUpdate = true;
        }

        if (hasUpdate) {
            resultObservable.value = result;
        }
    };

    public changeView(newView: Exclude<QueryHubView, QueryHubView.WorkItemEdit>): void;
    public changeView(newView: QueryHubView.WorkItemEdit, workItemId: number): void;
    public changeView(newView: string): void;
    public changeView(newView: QueryHubView, workItemId?: number): void {
        const previousView = this.view.value;
        this.previousView = previousView;
        this.view.value = newView;
        if (newView === QueryHubView.WorkItemEdit) {
            this.pageStateHistoryService.queueValueChange("id", String(workItemId), HistoryBehavior.replace);
            this.pageStateHistoryService.queueValueChange("queryId", this.queryId ?? "", HistoryBehavior.replace);
        } else if (previousView === QueryHubView.WorkItemEdit && this.queryId) {
            this.pageStateHistoryService.queueValueChange("id", this.queryId, HistoryBehavior.replace);

            // remove the "queryId" query parameter when navigating away from work item edit view
            const historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
            const currentState = historyService.getState();
            if (currentState["queryId"]) {
                delete currentState["queryId"];
                historyService.replaceState(currentState);
            }
        }
    }

    private getCurrentView(): QueryHubView | string {
        const historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const routeValues = historyService.getState();
        if (routeValues["view"].toLowerCase() === "resultsbyid") {
            routeValues["view"] = "query";
            historyService.replaceState(undefined, getQueryUrl(this.pageContext, routeValues["id"]));
        }
        const viewName = routeValues["view"]?.toLowerCase() || routeValues["_a"]?.toLowerCase();
        return viewName;
    }

    public getNotFetchedWorkItemIds(targetIds: number[], nextToFetchNumberOfWorkItems: number): number[] {
        // OneHop queries can have duplicating workItems, so we need to use Set here instead of array
        const notFetchedWorkItemIds = new Set<number>();
        for (const targetId of targetIds) {
            if (notFetchedWorkItemIds.size === nextToFetchNumberOfWorkItems) {
                break;
            }

            if (this.fetchedItemsIds.value[targetId] === undefined) {
                notFetchedWorkItemIds.add(targetId);
            }
        }

        return [...notFetchedWorkItemIds];
    }

    private updateFetchedItemsIds(workItemsIds: number[]): void {
        const oldFetchedItemsIds = this.fetchedItemsIds.value;
        for (const itemId of workItemsIds) {
            oldFetchedItemsIds[itemId] = itemId;
        }
        this.fetchedItemsIds.value = oldFetchedItemsIds;
    }

    public removeWorkItems(itemsIds: Set<number>): void {
        for (const key of itemsIds.keys()) {
            if (this.queryResultItemsMap.has(key)) {
                this.queryResultItemsMap.delete(key);
            }
            if (this.filteredQueryResultItemsMap.has(key)) {
                this.filteredQueryResultItemsMap.delete(key);
            }
        }
        this.queryResultItems.removeAll((item: IQueryResultItem) => itemsIds.has(item.value[CoreFieldRefNames.Id]));
    }

    private async fetchOrUpdateParentMap(items: IQueryResultItem[]): Promise<void> {
        const parentsToFetch: Set<number> = new Set();

        items.forEach(item => {
            const parentId: number | undefined = item.value[CoreFieldRefNames.Parent];
            if (!parentId || this.parentMap.has(parentId)) {
                return;
            }

            if (this.queryResultItemsMap.has(parentId)) {
                this.parentMap.set(parentId, this.queryResultItemsMap.get(parentId)!.value);
                return;
            }

            parentsToFetch.add(parentId);
        });

        if (!parentsToFetch.size) {
            return;
        }

        const parentItems = await this.fetchWorkItemsCore(Array.from(parentsToFetch));
        parentItems?.forEach(item => {
            const id = item.value[CoreFieldRefNames.Id];
            if (id && !this.parentMap.has(id)) {
                this.parentMap.set(id, item.value);
            }
        });
    }

    public getParentMap(): Map<number, IQueryResultItemData> {
        return this.parentMap;
    }
}

export function getQueryResultItem(columns: string[], row: string[]): IQueryResultItem {
    const item: IQueryResultItemData = {};

    for (let i = 0; i < columns.length; i++) {
        const key = columns[i];
        item[key] = row[i];
    }

    return new ObservableValue(item);
}

// get query items for just ids for loading row rendering
export function insertPlaceholderQueryItems(
    targetIds: number[],
    queryResultItems: IQueryResultItem[],
    columns: string[],
    queryResultItemsMap?: Map<number, IQueryResultItem>
) {
    for (let i = queryResultItems.length; i < targetIds.length; i++) {
        const row = new Array(columns.length).fill(undefined);
        row[0] = targetIds[i];
        const emptyItem = getQueryResultItem(columns, row);
        queryResultItems.push(emptyItem);
        if (queryResultItemsMap && !queryResultItemsMap.has(targetIds[i])) {
            queryResultItemsMap.set(targetIds[i], emptyItem);
        }
    }
}

const serviceName = "IQueryService";
Services.add(serviceName, { serviceFactory: QueryService });
